import { createMachine, transition, type AnyMachineSnapshot } from "xstate";
import { EXPANSION } from "@/data/tunables";
import { expansionEligible } from "@/systems/mailbox";
import type { GameState, TierId } from "@/state/GameState";

/**
 * M1 — TierExpansion (v0.10 RFC §3): the three-layer unlock spine (v0.3)
 * as a chart, one instance per expansion tier:
 *
 *   locked ──[markAtRunStart ladder]──▶ eligible
 *   eligible ──ARRIVE[reqs met]──▶ offered        (the letter lands)
 *   offered ──FILE──▶ purchasable                  (makePurchasable)
 *   purchasable ──BUY[affordable]──▶ open
 *
 * PURE per the Control Room contract. The early layers are driven by the
 * letter system: the PROJECTOR reuses the real predicates (expansionEligible,
 * the arrived-letter check), so the projected value cannot drift from the
 * mailbox's behavior. The ARRIVE/FILE transitions exist for the CHART (the
 * picture of the unlock spine) — no live caller fires them; only BUY is
 * live-arbitrated (purchaseTier routes through fireTierPurchase). (v0.10
 * review: the old comment claimed the GUARDS reused the predicates — wrong;
 * the reuse is in projectExpansion.)
 */

export type ExpansionTierId = keyof typeof EXPANSION;

export type ExpansionEvent =
  | { type: "ARRIVE"; requirementsMet: boolean }
  | { type: "FILE" }
  | { type: "BUY"; affordable: boolean };

export const expansionMachine = createMachine({
  types: {} as { events: ExpansionEvent },
  id: "tierExpansion",
  initial: "locked",
  states: {
    locked: {
      // The eligibility layer is prestige-gated data (markAtRunStart) —
      // it flips by PROJECTION, not by an in-run event.
    },
    eligible: {
      on: {
        ARRIVE: {
          target: "offered",
          guard: ({ event }) => event.type === "ARRIVE" && event.requirementsMet,
        },
      },
    },
    offered: {
      on: { FILE: { target: "purchasable", actions: [{ type: "makePurchasable" }] } },
    },
    purchasable: {
      on: {
        BUY: {
          target: "open",
          guard: ({ event }) => event.type === "BUY" && event.affordable,
          actions: [{ type: "openTier" }],
        },
      },
    },
    open: { type: "final" },
  },
});

/** The accreditation bootstrap (detox is not an expansion — the letter
 * opens it): locked ──ARRIVE──▶ offered ──FILE──▶ open. Chart-only today
 * (no player BUY); projected for the inspector/diagrams. */
export const accreditationMachine = createMachine({
  id: "detoxAccreditation",
  initial: "locked",
  states: {
    locked: { on: { ARRIVE: "offered" } },
    offered: { on: { FILE: { target: "open", actions: [{ type: "openTier" }] } } },
    open: { type: "final" },
  },
});

function offerLetterId(tier: ExpansionTierId): string {
  return `expansionOffer-${tier}`;
}

/** Where a tier stands, derived from the plain save (projection model —
 * same fields the mailbox/actions systems write). */
export function projectExpansion(state: GameState, tier: ExpansionTierId): AnyMachineSnapshot {
  let value: string;
  if (state.run.availableTiers.includes(tier as TierId)) value = "open";
  else if (state.run.purchasable?.tier === tier) value = "purchasable";
  else if (state.run.mailbox.arrived.some((m) => m.id === offerLetterId(tier))) value = "offered";
  else if (expansionEligible(state, tier)) value = "eligible";
  else value = "locked";
  return expansionMachine.resolveState({ value }) as AnyMachineSnapshot;
}

export type TierPurchaseFire =
  | { allowed: true; price: number; tier: ExpansionTierId }
  | { allowed: false; reason: "noOffer" | "alreadyOpen" | "funds"; price: number };

/** Ask the machine whether BUY may fire on the OPEN offer. Pure — the
 * caller spends, pushes the tier, ratchets, clears the offer. */
export function fireTierPurchase(state: GameState): TierPurchaseFire {
  const offer = state.run.purchasable;
  if (offer === null) return { allowed: false, reason: "noOffer", price: 0 };
  const tier = offer.tier as ExpansionTierId;
  if (state.run.availableTiers.includes(offer.tier)) {
    return { allowed: false, reason: "alreadyOpen", price: offer.price };
  }
  const before = projectExpansion(state, tier);
  const [after, actions] = transition(expansionMachine, before, {
    type: "BUY",
    affordable: state.run.funding >= offer.price,
  });
  const opened = actions.some((a) => (a as { type?: string }).type === "openTier");
  if (opened && after.value === "open") {
    return { allowed: true, price: offer.price, tier };
  }
  return { allowed: false, reason: "funds", price: offer.price };
}

/** Every expansion instance, enumerable for the inspector/diagrams. */
export function allExpansionTiers(): ExpansionTierId[] {
  return Object.keys(EXPANSION) as ExpansionTierId[];
}
